import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, NgModule} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {LoginComponent} from './routes/login/login.component';
import {HomeComponent} from './routes/home/home.component';
import {HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule} from '@angular/common/http';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {environment} from '../environments/environment';
import {NgxsModule, Store} from '@ngxs/store';
import {AppState} from './state/appState';
import {NgxsReduxDevtoolsPluginModule} from '@ngxs/devtools-plugin';
import {HeaderComponent} from './shared/components/header/header.component';
import {ReactiveFormsModule} from '@angular/forms';
import {AuthInterceptor} from './shared/interceptors/auth.interceptor';
import {ConfigService} from './shared/services/config/config.service';
import {ApiInterceptor} from './shared/interceptors/api.interceptor';
import {LoadingComponent} from './shared/components/loading/loading.component';
import {UserComponent} from './routes/home/user/user.component';
import {WebchargeComponent} from './routes/home/webcharge/webcharge.component';
import {PaginationComponent} from './shared/components/pagination/pagination.component';
import {OverviewUserTableComponent} from './routes/home/user/overview-user-table/overview-user-table.component';
import {MenuComponent} from './shared/components/menu/menu.component';
import {PaycardsComponent} from './routes/home/paycards/paycards.component';
import {PreorderComponent} from './routes/home/preorder/preorder.component';
import {AccountSettingsModalComponent} from './shared/components/modals/account-settings/account-settings.modal.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ToastrModule} from 'ngx-toastr';
import {UpdateUserModalComponent} from './shared/components/modals/update-user/update-user-modal.component';
import {DeleteUserModalComponent} from './shared/components/modals/delete-user/delete-user-modal.component';
import {CreateUserModalComponent} from './shared/components/modals/create-user/create-user-modal.component';
import {AdminOnlyDirective} from './shared/directives/admin-only.directive';
import {UserRestore} from './state/actions/appState.account.actions';
import {mergeMap, tap} from 'rxjs/operators';
import {PaycardDetailModalComponent} from './shared/components/modals/paycard-detail/paycard-detail-modal.component';
import {ConfirmModalComponent} from './shared/components/modals/confirm/confirm-modal.component';
import { PaycardsOverviewTableComponent } from './routes/home/paycards/paycards-overview-table/paycards-overview-table.component';
import {WebchargeDetailModalComponent} from './shared/components/modals/webcharge-detail/webcharge-detail-modal.component';
import {PreorderDetailModalComponent} from './shared/components/modals/preorder-detail/preorder-detail-modal.component';
import { WebchargeOverviewTableComponent } from './routes/home/webcharge/webcharge-overview-table/webcharge-overview-table.component';
import { PreorderOverviewTableComponent } from './routes/home/preorder/preoder-overview-table/preorder-overview-table.component';
import {PaymentDetailModalComponent} from './shared/components/modals/payment-detail/payment-detail-modal.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpBackend) {
  return new TranslateHttpLoader(new HttpClient(http));
}

function loadConfig(configService: ConfigService, store: Store): () => Promise<any> {
  return () => new Promise((resolve, reject) => {
    configService.getConfig().pipe(
      mergeMap(() => store.dispatch(new UserRestore())),
      tap(() => {
        resolve();
      })
    ).subscribe();
  });
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    HeaderComponent,
    LoadingComponent,
    UserComponent,
    WebchargeComponent,
    PaginationComponent,
    OverviewUserTableComponent,
    MenuComponent,
    PaycardsComponent,
    PreorderComponent,
    AccountSettingsModalComponent,
    UpdateUserModalComponent,
    DeleteUserModalComponent,
    CreateUserModalComponent,
    AdminOnlyDirective,
    PaycardDetailModalComponent,
    ConfirmModalComponent,
    PaycardsOverviewTableComponent,
    WebchargeDetailModalComponent,
    PreorderDetailModalComponent,
    WebchargeOverviewTableComponent,
    PreorderOverviewTableComponent,
    PaymentDetailModalComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgbModule,
    HttpClientModule,
    ReactiveFormsModule,
    NgxsModule.forRoot([
      AppState
    ], {
      developmentMode: !environment.production
    }),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    ToastrModule.forRoot({
      progressBar: true,
      closeButton: true
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      }
    })
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfig,
      deps: [
        ConfigService,
        Store
      ],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
